// Módulo Unificado de Atendimento (PRAT-64) — tipos do contrato da API.

export type AtendimentoModo = 'cliente' | 'assistido';

export type Consumo = 'viagem' | 'local';

// `cancelado` só aparece em "Meus pedidos" do cliente (a lista do operador não
// traz cancelados).
export type AtendimentoSituacao =
  'aguardando_pagamento' | 'confirmado' | 'salvo' | 'finalizado' | 'cancelado';

export type AtendimentoAcao = 'salvar' | 'finalizar';

export type AtendimentoMerchant = {
  id: string;
  slug: string;
  name: string;
  logo?: string | null;
};

export type AtendimentoOperador = {
  id: string;
  name: string;
};

// Forma de pagamento offline liberada no modo assistido.
export type PagamentoOffline = {
  id: string;
  name: string;
  type: string;
  image?: string | null;
  // Forma que gera NFC-e nesta loja (regra automática da nota).
  gera_nf?: boolean;
};

export type AtendimentoConfig = {
  merchant: AtendimentoMerchant;
  modulo_ativo: boolean;
  modo: AtendimentoModo;
  operador: AtendimentoOperador | null;
  caixa_aberto: boolean;
  mensagem_caixa: string | null;
  perguntar_consumo: boolean;
  mensagem_pagamento: string | null;
  pix_enabled: boolean;
  card_enabled: boolean;
  pagamentos_offline: PagamentoOffline[];
  // Só no assistido: a loja emite NFC-e / a geração automática está ligada.
  fiscal_ativo?: boolean;
  nf_automatica?: boolean;
  // PRAT-115: operador com a permissão "Editar e Excluir Pedidos" (cancelar venda).
  pode_cancelar?: boolean;
};

// Resposta da troca do código de uso único pelo JWT do operador. Fica no
// sessionStorage — nunca no cookie `x-auth-token` do cliente.
export type OperadorSession = {
  token: string;
  expira_em: string;
  merchant: Pick<AtendimentoMerchant, 'id' | 'slug' | 'name'>;
  operador: AtendimentoOperador;
};

export type AtendimentoCliente = {
  id: string;
  name: string;
  phone: string | null;
};

export type PagamentoVenda = {
  id: string;
  name: string;
  value: number;
};

// Fechamento da venda no assistido (pedido novo, venda continuada ou venda
// salva). Valores em reais. `emitir_nf` só vai quando o operador mexeu no
// "Emitir NFC-e"; sem ele a API aplica a regra automática da loja.
export type FinalizarVendaInput = {
  pagamentos: PagamentoVenda[];
  imprimir: boolean;
  desconto?: number;
  acrescimo?: number;
  cpf?: string;
  emitir_nf?: boolean;
};

// Campos que o pedido do atendimento acrescenta ao payload do place-order.
export type PedidoAtendimentoExtras = {
  consumo: Consumo | null;
  customer_id?: string | null;
  acao?: AtendimentoAcao;
  // Venda nova do operador: chave de envio idempotente (uuid v4, até 36). O app
  // grava a venda na hora e manda em segundo plano; o reenvio devolve a mesma.
  external_id?: string;
} & Partial<FinalizarVendaInput>;

export type AtendimentoPedidoResposta = {
  order_id: string;
  display_id: number;
  final_amount: number; // centavos
  situacao: AtendimentoSituacao;
  modo: AtendimentoModo;
  consumo: Consumo | null;
  status: number;
};

// PRAT-115: pagamento online da venda, para o aviso do cancelamento. PIX online
// não volta sozinho (`refund: 'manual'`): o lojista devolve pelo banco.
export type PagamentoOnlineCancelamento = {
  method: 'pix' | 'card';
  paid: boolean;
  amount: number;
  refund: 'manual' | 'automatic' | 'none';
};

export type CancelamentoInfo = {
  online_payment: PagamentoOnlineCancelamento | null;
  has_nfce: boolean;
  can_cancel_nfce: boolean;
  nfce_cancel_blocked_reason: string | null;
};

export type CancelarVendaInput = { motivo: string; pix_refund_ack?: boolean };

export type AtendimentoVenda = {
  order_id: string;
  display_id: number;
  cliente_nome: string | null;
  // PRAT-113: nome da venda balcão (só neste aparelho).
  nome_balcao?: string | null;
  total: number; // reais
  situacao: AtendimentoSituacao;
  consumo: Consumo | null;
  status: number;
  created_at: string;
  pagamentos: { name: string; value: number }[];
  items?: AtendimentoVendaItem[];
  modo?: AtendimentoModo;
};

// Complemento de um item da venda (GET do pedido). Preço por unidade.
export type AtendimentoItemComplemento = {
  category?: string | null;
  name: string;
  quantity: number;
  price?: number;
};

// Item da venda. A lista traz nome/quantidade/total; o GET do pedido também o
// preço da unidade, a observação do item e os complementos.
export type AtendimentoVendaItem = {
  name: string;
  quantity: number;
  total: number;
  price?: number;
  observation?: string | null;
  addons?: AtendimentoItemComplemento[];
};

// Venda salva aberta para continuar (GET do pedido, só operador + venda salva):
// o que o app precisa para remontar o carrinho.
export type EdicaoVendaItem = {
  id: string; // produto do cardápio
  name: string;
  quantity: number;
  observation: string | null;
  addons: { id: string; addon_name: string; addon_quantity: number }[];
  combo_choices: { group_id: string; item_id: string; quantity: number }[];
};

export type EdicaoVenda = {
  cliente: AtendimentoCliente | null;
  description: string | null;
  items: EdicaoVendaItem[];
};

export type AtendimentoVendaDetalhe = AtendimentoVenda & {
  edicao?: EdicaoVenda;
  // Observação da venda.
  description?: string | null;
  // false enquanto o pedido aguarda pagamento: a comanda não pode sair.
  imprimivel?: boolean;
};

export type SituacaoFiltro = 'salvo' | 'finalizado' | 'todos';

export const atendimentoEndpoint = (slug: string) =>
  `api/app/merchants/${encodeURIComponent(slug)}/atendimento`;

export const OPERADOR_ENDPOINT = 'api/app/atendimento/operador';

// Chaves de cache (React Query)
export const ATENDIMENTO_CONFIG_KEY = 'atendimento-config';
export const ATENDIMENTO_PEDIDOS_KEY = 'atendimento-pedidos';
export const ATENDIMENTO_PEDIDO_KEY = 'atendimento-pedido';
export const ATENDIMENTO_MEUS_PEDIDOS_KEY = 'atendimento-meus-pedidos';
export const ATENDIMENTO_PROXIMO_NUMERO_KEY = 'atendimento-proximo-numero';
